import React, { useContext } from 'react'
import Head from 'next/head'
import dynamic from 'next/dynamic'
import Prismic from 'prismic-javascript'
import { useRouter } from 'next/router'
import { RichText } from 'prismic-reactjs'
import GlobalContext from '../../global'
import getRef from '../../helpers/getRef'
import { repoName } from '../../config'
import { convertLangToPrismic } from '../../helpers/language'
import './page.scss'

import Header from '../../components/Header'
import Footer from '../../components/Footer'
import ArticleNavi from '../../components/ArticleNavi'
import linkResolver from '../../helpers/linkResolver'
import Suggestions from '../../components/Suggestions'
import OverviewTile from '../../components/OverviewTile'
import Link from '../../components/elements/link'
import FloatingButton from '../../components/detail/floatingButton'
const Social = dynamic(() => import('../../components/Social'), { ssr: false, loading: () => null })

function Page({ page: { data } }) {
  const router = useRouter()
  const global = useContext(GlobalContext)
  // derive title
  const title = data.title ? `${RichText.asText(data.title)} — ${global.data.site_name}` : global.data.site_name
  // derive image
  let image
  if (data.teaser_image && data.teaser_image.url) {
    image = data.teaser_image
  }
  // derive description
  const description = RichText.asText(data.summary)
  const headerFilled =
    data.body[0].slice_type !== 'title_image' &&
    data.body[0].slice_type !== 'title_video' &&
    data.body[0].slice_type !== 'slideshow'
  // related articles
  const relatedArticles = []
  if (data.related_article_1 && data.related_article_1.id) relatedArticles.push(data.related_article_1)
  if (data.related_article_2 && data.related_article_2.id) relatedArticles.push(data.related_article_2)
  if (data.related_article_3 && data.related_article_3.id) relatedArticles.push(data.related_article_3)
  return (
    <div className="site">
      <Head>
        <title>{title}</title>
        {description && <meta key="description" property="description" content={description} />}
        <meta key="og:type" property="og:type" content="website" />
        <meta key="og:site_name" property="og:site_name" content={title} />
        <meta key="og:title" property="og:title" content={title} />
        {description && <meta key="og:description" property="og:description" content={description} />}
        {image && <meta key="og:image" property="og:image" content={image.url} />}
        {image && <meta key="og:image:width" property="og:image:width" content={image.dimensions.width} />}
        {image && <meta key="og:image:height" property="og:image:height" content={image.dimensions.height} />}
        <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
        <meta key="twitter:title" name="twitter:title" content={title} />
        {description && <meta key="twitter:description" name="twitter:description" content={description} />}
        {image && <meta key="twitter:image" name="twitter:image" content={image.url} />}
      </Head>

      <Header alwaysFilled={headerFilled} />
      <div className={`article ${!headerFilled ? 'image' : 'padded'}`}>
        <FloatingButton />
        {data.body.map((module, i) => {
          switch (module.slice_type) {
            case 'single':
              return (
                <OverviewTile
                  key={`article-module-${i}`}
                  contents={[Object.assign(module.primary.article, { icon: module.primary.article_icon === 'icon' })]}
                  type="fullwidth"
                />
              )
            case 'double':
              return (
                <OverviewTile
                  key={`article-module-${i}`}
                  contents={[
                    Object.assign(module.primary.big_article, { icon: module.primary.big_icon === 'icon' }),
                    Object.assign(module.primary.small_article, { icon: module.primary.small_icon === 'icon' })
                  ]}
                  type={`double-${module.primary.big_article_position}`}
                />
              )
            case 'triple':
              return (
                <OverviewTile
                  key={`article-module-${i}`}
                  contents={[
                    Object.assign(module.primary.big_article, { icon: module.primary.big_icon === 'icon' }),
                    Object.assign(module.primary.small_article_1, { icon: module.primary.article1_icon === 'icon' }),
                    Object.assign(module.primary.small_article_2, { icon: module.primary.article2_icon === 'icon' })
                  ]}
                  type={`triple-${module.primary.big_article_position}`}
                />
              )
            case 'headline':
              const Headline = dynamic(() => import('../../components/detail/headline'), {
                ssr: true,
                loading: () => null
              })
              return <Headline key={`article-module-${i}`}>{RichText.render(module.primary.headline)}</Headline>
            case 'text_fullwidth':
              const TextFullWidth = dynamic(() => import('../../components/detail/textFullWidth'), {
                ssr: true,
                loading: () => null
              })
              return (
                <TextFullWidth key={`article-module-${i}`} alignment={module.primary.alignment}>
                  {RichText.render(module.primary.text, linkResolver)}
                </TextFullWidth>
              )
            case 'text_2-1':
              const TextTwoOne = dynamic(() => import('../../components/detail/textTwoOne'), {
                ssr: true,
                loading: () => null
              })
              return (
                <TextTwoOne
                  key={`article-module-${i}`}
                  bigCopy={RichText.render(module.primary['2_column_text'], linkResolver)}
                  smallCopy={RichText.render(module.primary['1_column_text'], linkResolver)}
                />
              )
            case 'quote':
              const Quote = dynamic(() => import('../../components/detail/quote'), {
                ssr: true,
                loading: () => null
              })
              return (
                <Quote
                  key={`article-module-${i}`}
                  quote={RichText.asText(module.primary.quote_text)}
                  author={RichText.asText(module.primary.author)}
                />
              )
            case 'image_2-1':
              const ImageTwoOne = dynamic(() => import('../../components/detail/imageTwoOne'), {
                ssr: true,
                loading: () => null
              })
              return (
                <ImageTwoOne
                  key={`article-module-${i}`}
                  bigImage={module.primary.big_image}
                  smallImage={module.primary.small_image}
                />
              )
            case '2_text_2_image':
              const TextTextDoubleImage = dynamic(() => import('../../components/detail/textTextDoubleImage'), {
                ssr: true,
                loading: () => null
              })
              return (
                <TextTextDoubleImage
                  key={`article-module-${i}`}
                  copy={RichText.render(module.primary.text, linkResolver)}
                  pictures={[
                    { image: module.primary.image_1, caption: module.primary.image_1_description },
                    { image: module.primary.image_2, caption: module.primary.image_2_description }
                  ]}
                />
              )
            case 'big_image_with_h2':
              const ImageTwoTextOne = dynamic(() => import('../../components/detail/imageTwoTextOne'), {
                ssr: true,
                loading: () => null
              })
              return (
                <ImageTwoTextOne
                  key={`article-module-${i}`}
                  image={module.primary.image}
                  copy={RichText.render(module.primary.headline, linkResolver)}
                />
              )
            case '2_images_1_text':
              if (module.primary.position_of_text === 'right') {
                const ImageImageText = dynamic(() => import('../../components/detail/imageImageText'), {
                  ssr: true,
                  loading: () => null
                })
                return (
                  <ImageImageText
                    key={`article-module-${i}`}
                    pictureOne={{ image: module.primary.image_1, caption: module.primary.image_1_description }}
                    pictureTwo={{ image: module.primary.image_2, caption: module.primary.image_2_description }}
                    copy={RichText.render(module.primary.text, linkResolver)}
                    centerCopy={true}
                  />
                )
              } else {
                const TextImageImage = dynamic(() => import('../../components/detail/textImageImage'), {
                  ssr: true,
                  loading: () => null
                })
                return (
                  <TextImageImage
                    key={`article-module-${i}`}
                    pictureOne={{ image: module.primary.image_1, caption: module.primary.image_1_description }}
                    pictureTwo={{ image: module.primary.image_2, caption: module.primary.image_2_description }}
                    copy={RichText.render(module.primary.text, linkResolver)}
                    centerCopy={false}
                  />
                )
              }
            case 'headline_2_text':
              const HeadlineTextText = dynamic(() => import('../../components/detail/headlineTextText'), {
                ssr: true,
                loading: () => null
              })
              return (
                <HeadlineTextText
                  key={`article-module-${i}`}
                  headline={RichText.render(module.primary.headline, linkResolver)}
                  copy={RichText.render(module.primary.text, linkResolver)}
                />
              )
            case 'slideshow':
              const Slideshow = dynamic(() => import('../../components/detail/slideshow'), {
                ssr: true,
                loading: () => null
              })
              return (
                <Slideshow
                  key={`article-module-${i}`}
                  pictures={module.items}
                  infinite={module.primary.infinite}
                  allowFullscreen={module.primary.allow_fullscreen}
                  {...module.primary}
                />
              )
            case 'video':
              const Video = dynamic(() => import('../../components/detail/video'), {
                ssr: true,
                loading: () => null
              })
              return (
                <Video
                  key={`article-module-${i}`}
                  title={RichText.render(module.primary.video_title, linkResolver)}
                  video={module.primary.video_embed}
                  poster={module.primary.poster}
                  size={module.primary.size}
                  caption={RichText.render(module.primary.description, linkResolver)}
                />
              )
            case 'audio':
              const Audio = dynamic(() => import('../../components/detail/audio'), {
                ssr: true,
                loading: () => null
              })
              return (
                <Audio
                  key={`article-module-${i}`}
                  audioObject={module.primary.mp3_link}
                  title={RichText.render(module.primary.audio_title, linkResolver)}
                />
              )
            case 'map':
              const Map = dynamic(() => import('../../components/detail/map'), {
                ssr: true,
                loading: () => null
              })
              return (
                <Map
                  key={`article-module-${i}`}
                  location={module.primary.map_location}
                  infoBig={RichText.render(module.primary.map_title, linkResolver)}
                  infoSmall={RichText.render(module.primary.text, linkResolver)}
                />
              )
            case '2_text_1_tall-image':
              const TextTextImage = dynamic(() => import('../../components/detail/textTextImage'), {
                ssr: true,
                loading: () => null
              })
              return (
                <TextTextImage
                  key={`article-module-${i}`}
                  copy={RichText.render(module.primary.text, linkResolver)}
                  image={module.primary.image}
                />
              )
            case 'event':
              const Event = dynamic(() => import('../../components/detail/event'), {
                ssr: true,
                loading: () => null
              })
              return (
                <Event
                  key={`article-module-${i}`}
                  headline={RichText.render(module.primary.headline, linkResolver)}
                  description={RichText.render(module.primary.description, linkResolver)}
                  dateInfo={RichText.render(module.primary.date_info, linkResolver)}
                  dates={module.items}
                />
              )
            case 'social_wall':
              const SocialWall = dynamic(() => import('../../components/detail/socialWall'), {
                ssr: false,
                loading: () => null
              })
              return <SocialWall key={`article-module-${i}`} code={module.primary.iframe_snippet} />
            case 'table':
              const Table = dynamic(() => import('../../components/detail/table'), {
                ssr: true,
                loading: () => null
              })
              return <Table key={`article-module-${i}`} head={module.primary} body={module.items} />
            case 'facts':
              const Facts = dynamic(() => import('../../components/detail/facts'), {
                ssr: true,
                loading: () => null
              })
              return (
                <Facts
                  key={`article-module-${i}`}
                  headline={RichText.render(module.primary.headline, linkResolver)}
                  facts={module.items}
                />
              )
            case 'fact___slideshow':
              const FactSlideshow = dynamic(() => import('../../components/detail/factSlideshow'), {
                ssr: true,
                loading: () => null
              })
              return <FactSlideshow key={`article-module-${i}`} module={module} />
            case 'icon___text':
              const IconText = dynamic(() => import('../../components/detail/iconText'), {
                ssr: true,
                loading: () => null
              })
              return (
                <IconText
                  key={`article-module-${i}`}
                  headline={RichText.render(module.primary.headline, linkResolver)}
                  body={RichText.render(module.primary.body1, linkResolver)}
                  icon={module.primary.icon1}
                  iconPosition={module.primary.icon_position}
                />
              )
            case 'responsive_image':
              const ResponsiveImage = dynamic(() => import('../../components/detail/responsiveImage'), {
                ssr: false,
                loading: () => null
              })
              return (
                <ResponsiveImage
                  main={module.primary.main_image}
                  mobile={module.primary.mobile_image}
                  scaling={module.primary.scaling}
                />
              )
            case '50_50_gallery':
              const Gallery5050 = dynamic(() => import('../../components/detail/gallery5050'), {
                ssr: true,
                loading: () => null
              })
              return <Gallery5050 key={`article-module-${i}`} items={module.items} />
            case 'title_image':
              const TitleImage = dynamic(() => import('../../components/detail/titleImage'), {
                ssr: true,
                loading: () => null
              })
              return (
                <TitleImage key={`article-module-${i}`} image={module.primary.image} height={module.primary.height} />
              )
            case 'title_video':
              const TitleVideo = dynamic(() => import('../../components/detail/titleVideo'), {
                ssr: true,
                loading: () => null
              })
              return <TitleVideo key={`article-module-${i}`} {...module.primary} />
            case 'title_intro':
              const TitleIntro = dynamic(() => import('../../components/detail/titleIntro'), {
                ssr: true,
                loading: () => null
              })
              return (
                <TitleIntro
                  key={`article-module-${i}`}
                  headline={RichText.render(module.primary.headline, linkResolver)}
                  text={RichText.render(module.primary.text, linkResolver)}
                  label={module.primary.label}
                  link={module.primary.link}
                />
              )

            case 'embed':
              const Embed = dynamic(() => import('../../components/detail/embed'), {
                ssr: true,
                loading: () => null
              })
              return <Embed {...module.primary} key={`article-module-${i}`} />
            case 'generic_widget':
              const Widget = dynamic(() => import('../../components/Widget'), {
                ssr: true,
                loading: () => null
              })
              return <Widget {...module.primary} key={`article-module-${i}`} />
            case 'inline_map':
              const InlineMap = dynamic(() => import('../../components/InlineMap'), {
                ssr: false,
                loading: () => null
              })
              return <InlineMap key={`article-module-${i}`} points={module.items} {...module.primary} />
            case 'zoom_image':
              const ZoomImage = dynamic(() => import('../../components/detail/zoomImage'), {
                ssr: true,
                loading: () => null
              })
              return <ZoomImage {...module.primary} key={`article-module-${i}`} />
            case 'teaser_block':
              const contents = module.items.map((tile, i) => {
                return {
                  data: {
                    id: i,
                    text: tile.text,
                    teaser_image: tile.image,
                    title: tile.title,
                    link: tile.link,
                    teaser_cta_label: tile.button_label,
                    subline: tile.subline,
                    icon: tile.article_icon,
                    shading: tile.shading,
                    textColor: tile.color
                  }
                }
              })
              let type = 'fullwidth'
              if (module.items.length === 2) {
                type = `double-${module.primary.big_article_position}`
              } else if (module.items.length === 3) {
                type = `triple-${module.primary.big_article_position}`
              }
              return <OverviewTile key={`article-module-${i}`} contents={contents} type={type} />
            default:
              console.info(`Frontend-Module for ${module.slice_type} missing.`)
          }
        })}
      </div>
      {relatedArticles.length > 0 && <Suggestions related={relatedArticles} />}
      <Social />
      {data.show_back === 'false' ? null : <ArticleNavi related={relatedArticles} />}
      <Footer />
    </div>
  )
}

Page.getInitialProps = async function(ctx) {
  try {
    // in rare cases images return undefined, this prevents errors
    if (!ctx.query.page) {
      return {}
    }
    const api = await Prismic.getApi(`https://${repoName}.prismic.io/api/v2`)
    const ref = getRef(api, ctx)
    const response = await api.query(Prismic.Predicates.at('my.page.uid', ctx.query.page), {
      ref,
      lang: convertLangToPrismic(ctx.query.lang.toLowerCase()),
      fetchLinks: [
        'page.title',
        'page.teaser_image',
        'page.subline',
        'page.teaser_cta_label',
        'page.icon_white',
        'page.icon_red'
      ]
    })
    if (!response) {
      throw 'no response'
    }
    if (ctx.res) {
      ctx.res.setHeader('Cache-Control', 's-maxage=1, stale-while-revalidate')
    }
    return { page: response.results[0] }
  } catch (e) {
    console.info(e)
    return { error: e }
  }
}

export default Page
